import React, { useRef, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

export default function ImageEditor({
  image,
  open,
  setOpen,
  width,
  height,
  job,
  getApprovalData,
  getApprovalList,
}) {
  const canvasRef = useRef(null);
  const [widthInch, setWidthInch] = useState(width || 200);
  const [heightInch, setHeightInch] = useState(height || 150);
  const rect = useRef({ x: 160, y: 80, w: 320, h: 220 });
  const drag = useRef(false);
  const resizeDir = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const getCanvasFile = () => {
    const canvas = canvasRef.current;

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const file = new File([blob], "recce-edited.png", {
          type: "image/png",
        });
        resolve(file);
      }, "image/png");
    });
  };

  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append("files", file);

    const token = localStorage.getItem("token");

    const response = await axios.post(
      `${process.env.REACT_APP_URL}/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data.data[0]; // 🔥 first uploaded URL
  };

  const handleSave = async () => {
    if (!widthInch || !heightInch) {
      Swal.fire({
        icon: "warning",
        title: "Width & Height required",
        timer: 1200,
        showConfirmButton: false,
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = "2000";
        },
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      // 🔥 1. Convert canvas → file
      const file = await getCanvasFile();

      // 🔥 2. Upload new image
      const uploadedImageUrl = await uploadSingleFile(file);

      // 🔥 3. Prepare updated job (NUMBER not string)
      // const updatedJob = {
      //   ...job,
      let newProductDimensionsAfterRecce = {
        width: parseFloat((widthInch / 12).toFixed(2)),
        height: parseFloat((heightInch / 12).toFixed(2)),
      };
      let imageOfRecceSpace = uploadedImageUrl;
      // let updatedBy = userId;
      // };

      // 🔥 4. Update job in DB
      const result = await axios.put(
        `${process.env.REACT_APP_URL}/jobs/updateJob/${job._id}`,
        {
          productDimensionsAfterRecce: newProductDimensionsAfterRecce,
          imageOfRecceSpace,
          // updatedBy,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (result.data.success) {
        // 🔥 5. Delete OLD image from S3 (SAFE WAY)
        if (
          job?.imageOfRecceSpace &&
          job.imageOfRecceSpace !== uploadedImageUrl
        ) {
          try {
            await axios.post(
              `${process.env.REACT_APP_URL}/deleteUpload`,
              { keys: [job.imageOfRecceSpace] },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              },
            );
          } catch (deleteError) {
            console.warn("Old image delete failed:", deleteError);
            // ❗ don't break flow
          }
        }

        // 🔥 6. Refresh UI data
        if (getApprovalData) await getApprovalData();
        if (getApprovalList) await getApprovalList();

        // 🔥 7. Success UI
        Swal.fire({
          icon: "success",
          title: "Recce image & dimensions updated!",
          showConfirmButton: false,
          timer: 1500,
          didOpen: () => {
            document.querySelector(".swal2-container").style.zIndex = "2000";
          },
        });

        // 🔥 8. Close modal
        setOpen(false);
      }
    } catch (error) {
      console.error("Error updating job:", error);

      Swal.fire({
        icon: "error",
        title: "Error updating job!",
        text: error?.response?.data?.message || "Something went wrong",
        timer: 2000,
        showConfirmButton: false,
        didOpen: () => {
          document.querySelector(".swal2-container").style.zIndex = "2000";
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    const img = new Image();

    img.crossOrigin = "anonymous";
    img.src = `${image}?t=${Date.now()}`; // 🔥 cache fix

    img.onload = () => {
      imgRef.current = img;
      draw();
    };
  }, [open, image]);

  useEffect(() => {
    if (imgRef.current) draw();
  }, [widthInch, heightInch]);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = imgRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (img) ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const { x, y, w, h } = rect.current;

    // Selection fill
    ctx.fillStyle = "transparent";
    ctx.fillRect(x, y, w, h);

    ctx.save();
    ctx.strokeStyle = "rgb(65,140,255)";
    ctx.lineWidth = 3;
    ctx.save();

    ctx.strokeStyle = "rgb(65,140,255)";
    ctx.lineWidth = 4;

    ctx.strokeRect(
      Math.round(x) + 0.5,
      Math.round(y) + 0.5,
      Math.round(w),
      Math.round(h),
    );

    ctx.restore();
    ctx.restore();

    // Handle dots
    const handleSize = 18; // 🔥 increase size (important)

    // [
    //   [x, y],
    //   [x + w, y],
    //   [x, y + h],
    //   [x + w, y + h],
    // ].forEach(([hx, hy]) => {
    //   ctx.fillStyle = "#418CFFDC";

    //   ctx.fillRect(
    //     hx - handleSize / 2,
    //     hy - handleSize / 2,
    //     handleSize,
    //     handleSize,
    //   );
    // });

    // Width label
    drawPill(ctx, `${widthInch} Inch`, x + w / 2, y - 14);
    // Height label (rotated)
    ctx.save();

    // Center of rectangle height
    ctx.translate(x - 30, y + h / 2);

    ctx.rotate(-Math.PI / 2);

    drawPill(ctx, `${heightInch} Inch`, 0, 16);

    ctx.restore();
  };

  const drawPill = (ctx, text, cx, cy) => {
    ctx.font = "200 16px 'Arial'";
    const tw = ctx.measureText(text).width;
    const pw = tw + 20,
      ph = 26,
      px = cx - pw / 2,
      py = cy - ph / 2;
    ctx.fillStyle = "#418CFFDC";
    ctx.beginPath();
    roundRect(ctx, px, py, pw, ph, 0);
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(text, cx, cy + 0.5);
  };

  const roundRect = (ctx, x, y, w, h, r) => {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.lineTo(x + w, y + h - r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.lineTo(x + r, y + h);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.lineTo(x, y + r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  };

  const isNear = (mx, my, px, py) =>
    Math.abs(mx - px) < 12 && Math.abs(my - py) < 12;

  const getDir = (mx, my) => {
    const { x, y, w, h } = rect.current;
    if (isNear(mx, my, x, y)) return "tl";
    if (isNear(mx, my, x + w, y)) return "tr";
    if (isNear(mx, my, x, y + h)) return "bl";
    if (isNear(mx, my, x + w, y + h)) return "br";
    if (mx > x && mx < x + w && my > y && my < y + h) return "move";
    return null;
  };

  const handleMouseDown = (e) => {
    const { offsetX, offsetY } = e.nativeEvent;
    const d = getDir(offsetX, offsetY);
    if (d === "move") {
      drag.current = true;
      dragOffset.current = {
        x: offsetX - rect.current.x,
        y: offsetY - rect.current.y,
      };
    } else if (d) {
      resizeDir.current = d;
    }
  };

  const handleMouseMove = (e) => {
    const { offsetX: ox, offsetY: oy } = e.nativeEvent;
    const r = rect.current;
    const canvas = canvasRef.current;
    const MIN_W = 60;
    const MIN_H = 40;

    const LABEL_MARGIN_TOP = 50;
    const LABEL_MARGIN_LEFT = 50;
    const PADDING = 20;
    if (drag.current) {
      const newX = ox - dragOffset.current.x;
      const newY = oy - dragOffset.current.y;

      // 🔒 Clamp inside canvas
      r.x = Math.max(50, Math.min(newX, canvas.width - r.w - 20));
      r.y = Math.max(50, Math.min(newY, canvas.height - r.h - 20));
    }
    if (resizeDir.current) {
      if (resizeDir.current === "br") {
        r.w = Math.min(Math.max(MIN_W, ox - r.x), canvas.width - r.x - PADDING);

        r.h = Math.min(
          Math.max(MIN_H, oy - r.y),
          canvas.height - r.y - PADDING,
        );
      }
      if (resizeDir.current === "tl") {
        const nx = Math.max(LABEL_MARGIN_LEFT, Math.min(ox, r.x + r.w - MIN_W));

        const ny = Math.max(LABEL_MARGIN_TOP, Math.min(oy, r.y + r.h - MIN_H));

        r.w += r.x - nx;
        r.h += r.y - ny;
        r.x = nx;
        r.y = ny;
      }
      if (resizeDir.current === "tr") {
        const ny = Math.max(LABEL_MARGIN_TOP, Math.min(oy, r.y + r.h - MIN_H));

        r.w = Math.min(Math.max(MIN_W, ox - r.x), canvas.width - r.x - PADDING);

        r.h += r.y - ny;
        r.y = ny;
      }
      if (resizeDir.current === "bl") {
        const nx = Math.max(LABEL_MARGIN_LEFT, Math.min(ox, r.x + r.w - MIN_W));

        r.w += r.x - nx;
        r.x = nx;

        r.h = Math.min(
          Math.max(MIN_H, oy - r.y),
          canvas.height - r.y - PADDING,
        );
      }
    }
    draw();
  };

  const handleMouseUp = () => {
    drag.current = false;
    resizeDir.current = null;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-blue-950/40">
      {/* Modal */}
      <div className="w-[980px] max-w-[95vw] bg-white rounded-2xl overflow-hidden shadow-2xl shadow-blue-200/60 border border-blue-100">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700">
          <div className="flex items-center gap-3">
            {/* Icon badge */}
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center text-base border border-white/30">
              📐
            </div>
            <div>
              <h2 className="text-white text-sm font-bold tracking-wide">
                Edit Recce Tool
              </h2>
              <p className="text-blue-200 text-[10px] tracking-widest uppercase mt-0.5 font-medium">
                Interactive Selection
              </p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="w-8 h-8 rounded-lg bg-white/15 border border-white/25 text-white/70 hover:bg-white/25 hover:text-white flex items-center justify-center text-sm font-medium transition-all duration-150"
          >
            ✕
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-3 px-6 py-3 bg-blue-50 border-b border-blue-100">
          {/* Width input */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-blue-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all duration-150 shadow-sm">
            <span className="text-blue-400 text-xs font-bold">↔</span>
            <label className="text-blue-400 text-[9px] tracking-[0.1em] uppercase font-semibold whitespace-nowrap">
              Width
            </label>
            <input
              type="number"
              value={widthInch}
              onChange={(e) => setWidthInch(e.target.value)}
              className="bg-transparent border-none outline-none text-blue-800 text-sm w-14 text-right font-semibold"
            />
            <span className="text-blue-400 text-[10px] font-medium">inch</span>
          </div>

          {/* Height input */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-blue-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all duration-150 shadow-sm">
            <span className="text-blue-400 text-xs font-bold">↕</span>
            <label className="text-blue-400 text-[9px] tracking-[0.1em] uppercase font-semibold whitespace-nowrap">
              Height
            </label>
            <input
              type="number"
              value={heightInch}
              onChange={(e) => setHeightInch(e.target.value)}
              className="bg-transparent border-none outline-none text-blue-800 text-sm w-14 text-right font-semibold"
            />
            <span className="text-blue-400 text-[10px] font-medium">inch</span>
          </div>
        </div>

        {/* ── Canvas ── */}
        <div
          className="relative border-t border-blue-100 flex items-center justify-center"
          style={{
            background:
              "repeating-linear-gradient(0deg,transparent,transparent 23px,#eff6ff 23px,#eff6ff 24px)," +
              "repeating-linear-gradient(90deg,transparent,transparent 23px,#eff6ff 23px,#eff6ff 24px)," +
              "#f8faff",
          }}
        >
          <canvas
            ref={canvasRef}
            width={900}
            height={450}
            className="block cursor-crosshair"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          />
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end px-6 py-3 bg-white border-t border-blue-100">
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-lg text-blue-500 text-xs font-semibold border border-blue-200 bg-white hover:bg-blue-50 transition-all duration-150"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className={`flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-white text-xs font-bold shadow-md transition-all duration-150 ${
                loading
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 active:scale-[0.98] shadow-blue-200"
              }`}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Saving...
                </>
              ) : (
                "✓ Apply Measurement"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
